import React, { useState, useCallback, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { FaSearch, FaFileInvoice, FaFileExcel } from 'react-icons/fa'
import { FiRefreshCw } from 'react-icons/fi'
import { MdOutlineReceipt } from 'react-icons/md'
import toast from 'react-hot-toast'
import api from '../../libs/axios'
import { Pagination } from '../../utils/pagination.utils'
import { SelectWithSearch } from '../../utils/select-with-search'
import type { SelectOption } from '../../utils/select-with-search'
import { useDebouncedCallback } from '../../hooks/useDebounce'
import { ExcelDownload } from '../../utils/excel.utils'

interface POSite {
  id: number
  site_id: string
  site_name?: string
  reference_number?: string
  project?: { id: number; project_name: string; activity?: { activity_name: string } }
}

interface PORecord {
  id: number
  po_number: string
  po_date: string
  po_document?: string | null
  item: string
  item_description: string
  status: string
  working_month?: string | null
  sales_month?: string | null
  po_received_status?: string | null
  invoice_number?: string | null
  invoice_date?: string | null
  invoice_document?: string | null
  invoice_basic_amount?: number | null
  invoice_gst_amount?: number | null
  invoice_total_amount?: number | null
  vendor?: { id: number; vendor_full_name: string; vendor_short_name: string; vendor_code?: string }
  site?: {
    id: number
    site_id: string
    site_name?: string
    reference_number?: string
    survey_workflow_status: string
    project?: { id: number; project_name: string; activity?: { activity_name: string } }
  }
  sites?: POSite[]
  site_count?: number
  billing_coordinator?: { id: number; first_name: string; last_name: string }
}

interface PaginationData {
  page: number
  limit: number
  total_count: number
  total_pages: number
  has_next_page: boolean
  has_prev_page: boolean
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  po_generated:      { label: 'PO Issued',         color: 'bg-green-100 text-green-700' },
  invoice_submitted: { label: 'Invoice Submitted',  color: 'bg-blue-100 text-blue-700' },
  invoice_booked:    { label: 'Invoice Booked',     color: 'bg-emerald-100 text-emerald-700' },
  invoice_rejected:  { label: 'Invoice Rejected',   color: 'bg-red-100 text-red-700' },
}

const ALL_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'po_generated', label: 'PO Issued' },
  { value: 'invoice_submitted', label: 'Invoice Submitted' },
  { value: 'invoice_booked', label: 'Invoice Booked' },
  { value: 'invoice_rejected', label: 'Invoice Rejected' },
]

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

// Number of site IDs shown before the "View More" toggle kicks in (item 1d).
const SITE_ID_PREVIEW = 3

const HEADERS = [
  'PO Number', 'PO Date', 'Vendor', 'Site Qty', 'Site ID', 'Site Name', 'Project', 'Activity',
  'Item Name', 'Item Description', 'Working Month', 'Sales Month', 'PO Received',
  'PO Doc', 'Invoice Number', 'Invoice Date',
  'Vendor Invoice Stages', 'Coordinator',
]

// Resolve the list of sites a PO covers (falls back to the legacy single site).
const poSites = (rec: PORecord): POSite[] =>
  rec.sites && rec.sites.length ? rec.sites : (rec.site ? [rec.site as POSite] : [])

export const POIssuedPage: React.FC = () => {
  const [records, setRecords] = useState<PORecord[]>([])
  const [pagination, setPagination] = useState<PaginationData>({ page: 1, limit: 20, total_count: 0, total_pages: 1, has_next_page: false, has_prev_page: false })
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [vendorFilter, setVendorFilter] = useState<SelectOption | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const [exportData, setExportData] = useState<any[]>([])
  const [exporting, setExporting] = useState(false)
  const [expandedSites, setExpandedSites] = useState<Set<number>>(new Set())
  const [siteExportOpen, setSiteExportOpen] = useState(false)
  const [siteExportData, setSiteExportData] = useState<any[]>([])
  const [siteExportName, setSiteExportName] = useState('po_sites')
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  const toggleExpanded = (id: number) =>
    setExpandedSites((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  // Download the full site list covered by a single PO (item 1c).
  const handleDownloadSites = async (rec: PORecord) => {
    setDownloadingId(rec.id)
    try {
      const res = await api.get(`/billing/vendor-site-invoices/${rec.id}/sites`)
      const rows: any[] = res.data?.data?.rows ?? []
      if (!rows.length) { toast.error('No sites found for this PO'); return }
      // Per-PO site Excel — only the columns requested in item 6.
      setSiteExportData(rows.map((s) => ({
        'Site ID': s.site_id ?? '—',
        'Site Name': s.site_name ?? '—',
        'Activity': s.activity?.activity_name ?? '—',
        'Project': s.project?.project_short_name ?? s.project?.project_name ?? '—',
        'Final Vendor Name': s.final_vendor_name ?? s.vendor?.vendor_full_name ?? '—',
        'PO Number': s.po_number ?? '—',
        'PO Date': fmt(s.po_date),
        'Item Name': s.item ?? '—',
        'Item Description': s.item_description ?? '—',
      })))
      setSiteExportName(`po_${rec.po_number}_sites`)
      setSiteExportOpen(true)
    } catch {
      toast.error('Failed to load PO sites')
    } finally {
      setDownloadingId(null)
    }
  }

  const fetchRecords = useCallback(async (overrides: { page?: number; limit?: number; search?: string; status?: string; vendor_id?: string } = {}) => {
    setLoading(true)
    try {
      const params: any = {
        page: overrides.page ?? pagination.page,
        limit: overrides.limit ?? pagination.limit,
      }
      const s = overrides.search !== undefined ? overrides.search : search
      if (s) params.search = s
      const st = overrides.status !== undefined ? overrides.status : statusFilter
      if (st) params.status = st
      const vid = overrides.vendor_id !== undefined ? overrides.vendor_id : vendorFilter?.value
      if (vid) params.vendor_id = vid

      const res = await api.get('/billing/vendor-site-invoices', { params })
      if (res.data.success) {
        setRecords(res.data.data.rows || [])
        setPagination(res.data.data.pagination || pagination)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [pagination.page, pagination.limit, search, statusFilter, vendorFilter])

  useEffect(() => { fetchRecords({ page: 1 }) }, [])

  const debouncedSearch = useDebouncedCallback((val: string) => fetchRecords({ search: val, page: 1 }), 400)

  const handleExport = async () => {
    setExporting(true)
    try {
      let all: PORecord[] = []
      let page = 1
      // Cap at 100 pages (10k POs) as a safety valve.
      while (page <= 100) {
        const params: any = { page, limit: 100 }
        if (search) params.search = search
        if (statusFilter) params.status = statusFilter
        if (vendorFilter?.value) params.vendor_id = vendorFilter.value
        const res = await api.get('/billing/vendor-site-invoices', { params })
        const rows: PORecord[] = res.data?.data?.rows ?? []
        all = [...all, ...rows]
        if (rows.length < 100) break
        page++
      }
      setExportData(all.map((rec) => {
        const sl = poSites(rec)
        return {
          'PO Number': rec.po_number,
          'PO Date': fmt(rec.po_date),
          'Vendor': rec.vendor?.vendor_full_name ?? '—',
          'Vendor Code': rec.vendor?.vendor_code ?? '—',
          'Site Qty': sl.length,
          'Site IDs': sl.map((s) => s.site_id).join(', '),
          'Site Names': sl.map((s) => s.site_name ?? '').filter(Boolean).join(', '),
          'Project': sl[0]?.project?.project_name ?? '—',
          'Activity': sl[0]?.project?.activity?.activity_name ?? '—',
          'Item Name': rec.item,
          'Item Description': rec.item_description,
          'Working Month': rec.working_month ?? '—',
          'Sales Month': rec.sales_month ?? '—',
          'PO Received': rec.po_received_status ?? '—',
          'Invoice Number': rec.invoice_number ?? '—',
          'Invoice Date': fmt(rec.invoice_date),
          'Vendor Invoice Stage': STATUS_CONFIG[rec.status]?.label ?? rec.status,
          'Coordinator': rec.billing_coordinator
            ? `${rec.billing_coordinator.first_name} ${rec.billing_coordinator.last_name}`
            : '—',
        }
      }))
      setExportOpen(true)
    } catch {
      toast.error('Failed to export')
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <Helmet><title>PO Issued – Billing</title></Helmet>
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <MdOutlineReceipt className="w-6 h-6 text-blue-700" />PO Issued
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                All purchase orders generated — {pagination.total_count} record{pagination.total_count !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting}
                className="inline-flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
              >
                <FaFileExcel className="w-4 h-4" />{exporting ? 'Exporting…' : 'Export Excel'}
              </button>
              <button
                type="button"
                onClick={() => fetchRecords({ page: 1 })}
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50"
              >
                <FiRefreshCw className="w-4 h-4" />Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50/30 flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by site ID, PO number..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); debouncedSearch(e.target.value) }}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); fetchRecords({ status: e.target.value, page: 1 }) }}
            className="px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500"
          >
            {ALL_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="w-full sm:w-64">
            <SelectWithSearch
              placeholder="Filter by vendor..."
              value={vendorFilter}
              onChange={(opt) => { setVendorFilter(opt); fetchRecords({ vendor_id: opt ? String(opt.value) : '', page: 1 }) }}
              apiEndpoint="/vendor/list"
              dataKey="vendors"
              labelKey="vendor_full_name"
              valueKey="id"
              searchParam="search"
              className="w-full"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {HEADERS.map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={HEADERS.length} className="py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                      <span className="text-sm text-gray-500">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={HEADERS.length} className="py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
                        <FaFileInvoice className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">No PO records found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                records.map((rec) => {
                  const statusCfg = STATUS_CONFIG[rec.status] || { label: rec.status, color: 'bg-gray-100 text-gray-600' }
                  return (
                    <tr key={rec.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-3 font-mono font-semibold text-blue-700 whitespace-nowrap text-xs">{rec.po_number}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fmt(rec.po_date)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                        {rec.vendor ? (
                          <div>
                            <div className="font-medium">{rec.vendor.vendor_full_name}</div>
                            {rec.vendor.vendor_code && <div className="text-gray-400">{rec.vendor.vendor_code}</div>}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-3 text-xs text-center">
                        {(() => {
                          const sl = poSites(rec)
                          const n = rec.site_count ?? sl.length
                          return (
                            <div className="flex items-center justify-center gap-1.5">
                              <span
                                className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold"
                                title={sl.map((s) => s.site_id).join(', ')}
                              >
                                {n}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleDownloadSites(rec)}
                                disabled={downloadingId === rec.id}
                                title="Download site list (Excel)"
                                className="text-green-600 hover:text-green-800 disabled:opacity-40"
                              >
                                {downloadingId === rec.id
                                  ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-b border-green-600" />
                                  : <FaFileExcel className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          )
                        })()}
                      </td>
                      <td className="px-3 py-3 text-xs font-mono text-gray-700 max-w-[200px]">
                        {(() => {
                          const ids = poSites(rec).map((s) => s.site_id)
                          if (ids.length === 0) return '—'
                          const expanded = expandedSites.has(rec.id)
                          const shown = expanded ? ids : ids.slice(0, SITE_ID_PREVIEW)
                          const hidden = ids.length - shown.length
                          return (
                            <div className="space-y-1">
                              <div className="whitespace-normal break-words">{shown.join(', ')}</div>
                              {ids.length > SITE_ID_PREVIEW && (
                                <button
                                  type="button"
                                  onClick={() => toggleExpanded(rec.id)}
                                  className="text-blue-600 hover:text-blue-800 font-sans font-medium"
                                >
                                  {expanded ? 'View Less' : `+${hidden} View More`}
                                </button>
                              )}
                            </div>
                          )
                        })()}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-900 max-w-[200px]">
                        <span title={poSites(rec).map((s) => s.site_name).filter(Boolean).join(', ')} className="line-clamp-2">
                          {poSites(rec).map((s) => s.site_name).filter(Boolean).join(', ') || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{poSites(rec)[0]?.project?.project_name || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-500">{poSites(rec)[0]?.project?.activity?.activity_name || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 font-medium">{rec.item}</td>
                      <td className="px-3 py-3 text-xs text-gray-500 max-w-[160px] truncate" title={rec.item_description}>{rec.item_description}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{rec.working_month || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{rec.sales_month || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 capitalize">{rec.po_received_status || '—'}</td>
                      <td className="px-3 py-3">
                        {rec.po_document ? (
                          <a href={rec.po_document} target="_blank" rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-xs underline">View PO</a>
                        ) : <span className="text-gray-300 text-xs">-</span>}
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-gray-900">{rec.invoice_number || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fmt(rec.invoice_date)}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
                          {statusCfg.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                        {rec.billing_coordinator
                          ? `${rec.billing_coordinator.first_name} ${rec.billing_coordinator.last_name}`
                          : '—'}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          pagination={pagination as any}
          onPageChange={(p) => fetchRecords({ page: p })}
          onLimitChange={(l) => { setPagination((prev) => ({ ...prev, limit: l })); fetchRecords({ page: 1, limit: l }) }}
        />
      </div>

      <ExcelDownload
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
        data={exportData}
        filename="po_issued"
        title="Export PO Issued"
        description="Download the PO Issued records in Excel format"
      />

      <ExcelDownload
        isOpen={siteExportOpen}
        onClose={() => setSiteExportOpen(false)}
        data={siteExportData}
        filename={siteExportName}
        title="Export PO Site List"
        description="Download the list of sites covered by this PO"
      />
    </>
  )
}

export default POIssuedPage


